import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  MenuItem,
  FormControlLabel,
  Switch
} from '@mui/material';
import { tenantApiFetch } from './tenantApi';
import { useTenantAuth } from '../context/TenantAuthContext';

const empty = {
  email: '',
  password: '',
  first_name: '',
  last_name: '',
  full_name: '',
  mobile_number: '',
  roleIds: [],
  invite: false
};

export default function TenantUserFormPage() {
  const { userId } = useParams();
  const isEdit = Boolean(userId);
  const navigate = useNavigate();
  const { user, ready, hasPermission } = useTenantAuth();

  const [form, setForm] = useState(empty);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (ready && !user) {
      navigate('/tenant/login', { replace: true });
    }
  }, [ready, user, navigate]);

  useEffect(() => {
    if (!ready || !user) return;
    if (isEdit && !hasPermission('tenant.users.update')) {
      navigate('/tenant/users', { replace: true });
    }
    if (!isEdit && !hasPermission('tenant.users.create')) {
      navigate('/tenant/users', { replace: true });
    }
  }, [ready, user, isEdit, hasPermission, navigate]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await tenantApiFetch('/api/tenant/roles?forSelect=1');
        const json = await res.json().catch(() => ({}));
        if (res.ok && !cancelled) setRoles(json.roles || []);
      } catch (_e) {
        /* ignore */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const load = useCallback(async () => {
    if (!isEdit) return;
    setLoading(true);
    setError(null);
    try {
      const res = await tenantApiFetch(`/api/tenant/users/${userId}`);
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Failed to load user');
      const u = json.user;
      setForm({
        email: u.email || '',
        password: '',
        first_name: u.first_name || '',
        last_name: u.last_name || '',
        full_name: u.full_name || '',
        mobile_number: u.mobile_number || '',
        roleIds: (u.role_ids || []).map(Number),
        invite: false
      });
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [isEdit, userId]);

  useEffect(() => {
    load();
  }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      if (!isEdit) {
        if (form.invite) {
          const res = await tenantApiFetch('/api/tenant/users', {
            method: 'POST',
            body: {
              email: form.email.trim(),
              invite: true,
              first_name: form.first_name.trim() || null,
              last_name: form.last_name.trim() || null,
              full_name: form.full_name.trim() || null,
              mobile_number: form.mobile_number.trim() || null,
              roleIds: form.roleIds
            }
          });
          const json = await res.json().catch(() => ({}));
          if (!res.ok) throw new Error(json.error || 'Invite failed');
          navigate(`/tenant/users/${json.user.id}`);
        } else {
          if (!form.password) throw new Error('Password is required unless inviting');
          const res = await tenantApiFetch('/api/tenant/users', {
            method: 'POST',
            body: {
              email: form.email.trim(),
              password: form.password,
              first_name: form.first_name.trim() || null,
              last_name: form.last_name.trim() || null,
              full_name: form.full_name.trim() || null,
              mobile_number: form.mobile_number.trim() || null,
              roleIds: form.roleIds
            }
          });
          const json = await res.json().catch(() => ({}));
          if (!res.ok) throw new Error(json.error || 'Create failed');
          navigate(`/tenant/users/${json.user.id}`);
        }
      } else {
        const body = {
          email: form.email.trim(),
          first_name: form.first_name.trim() || null,
          last_name: form.last_name.trim() || null,
          full_name: form.full_name.trim() || null,
          mobile_number: form.mobile_number.trim() || null,
          roleIds: form.roleIds
        };
        if (form.password) body.password = form.password;
        const res = await tenantApiFetch(`/api/tenant/users/${userId}`, {
          method: 'PATCH',
          body
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(json.error || 'Save failed');
        navigate(`/tenant/users/${userId}`);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {isEdit ? 'Edit user' : 'Add user'}
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      <Paper component="form" onSubmit={submit} sx={{ p: 3, maxWidth: 560 }}>
        {!isEdit && (
          <FormControlLabel
            control={
              <Switch
                checked={form.invite}
                onChange={(e) => set('invite', e.target.checked)}
              />
            }
            label="Invite only (no password yet — user cannot sign in until you set a password and activate)"
            sx={{ mb: 2, display: 'block' }}
          />
        )}
        <TextField
          label="Email"
          type="email"
          required
          fullWidth
          margin="normal"
          value={form.email}
          onChange={(e) => set('email', e.target.value)}
        />
        {!isEdit && !form.invite && (
          <TextField
            label="Password"
            type="password"
            required
            fullWidth
            margin="normal"
            value={form.password}
            onChange={(e) => set('password', e.target.value)}
          />
        )}
        {isEdit && (
          <TextField
            label="New password (optional)"
            type="password"
            fullWidth
            margin="normal"
            value={form.password}
            onChange={(e) => set('password', e.target.value)}
            helperText="Leave blank to keep current password"
          />
        )}
        <TextField
          label="First name"
          fullWidth
          margin="normal"
          value={form.first_name}
          onChange={(e) => set('first_name', e.target.value)}
        />
        <TextField
          label="Last name"
          fullWidth
          margin="normal"
          value={form.last_name}
          onChange={(e) => set('last_name', e.target.value)}
        />
        <TextField
          label="Full name (optional — derived from first + last if empty)"
          fullWidth
          margin="normal"
          value={form.full_name}
          onChange={(e) => set('full_name', e.target.value)}
        />
        <TextField
          label="Mobile number"
          fullWidth
          margin="normal"
          value={form.mobile_number}
          onChange={(e) => set('mobile_number', e.target.value)}
        />
        <FormControl fullWidth margin="normal">
          <InputLabel id="roles-label">Roles</InputLabel>
          <Select
            labelId="roles-label"
            multiple
            value={form.roleIds}
            onChange={(e) => set('roleIds', e.target.value)}
            input={<OutlinedInput label="Roles" />}
            renderValue={(selected) =>
              roles
                .filter((r) => selected.includes(r.id))
                .map((r) => r.name)
                .join(', ')
            }
          >
            {roles.map((r) => (
              <MenuItem key={r.id} value={r.id}>
                {r.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
          <Button type="submit" variant="contained" disabled={saving}>
            {saving ? <CircularProgress size={22} /> : isEdit ? 'Save' : form.invite ? 'Send invite' : 'Create'}
          </Button>
          <Button onClick={() => navigate(isEdit ? `/tenant/users/${userId}` : '/tenant/users')}>
            Cancel
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}
